/**
 * Shared color utilities for CardV2 components.
 */

/**
 * Determine if a hex color is dark based on W3C relative luminance (sRGB).
 * Uses linearized sRGB values for perceptually accurate luminance calculation.
 */
export function isColorDark(hexColor?: string | null): boolean {
  if (!hexColor) return false;
  const hex = hexColor.replace('#', '');
  if (hex.length < 6) return false;
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // sRGB linearization
  const [rL, gL, bL] = [r, g, b].map((v) => {
    const sv = v / 255;
    return sv <= 0.03928 ? sv / 12.92 : ((sv + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * rL + 0.7152 * gL + 0.0722 * bL;
  return luminance < 0.5;
}

/** Primary text color that contrasts with the given background */
export function getTextPrimary(darkBg: boolean): string {
  return darkBg ? '#ebe6d6' : '#181800';
}

/** Secondary/muted text color that contrasts with the given background */
export function getTextSecondary(darkBg: boolean): string {
  return darkBg ? '#c8c0ac' : '#7a7359';
}

/**
 * Parse a hex color string to RGB components.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
}

/**
 * Convert RGB to HSL.
 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h, s, l };
}

/**
 * Convert HSL to hex color string.
 */
function hslToHex(h: number, s: number, l: number): string {
  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (t < 0) tt = t + 1;
    else if (t > 1) tt = t - 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = l;
    g = l;
    b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (v: number) => {
    const hex = Math.round(v * 255).toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Derive a subtle vertical gradient from a base color (dominant image color).
 * Uses the base color directly as the "from" stop and a slightly darker version
 * as the "to" stop, preserving the card's unique visual identity.
 *
 * The old card system used backgroundColor as a solid fill. This produces a
 * gradient that keeps the original color recognizable while adding subtle depth.
 */
export function deriveGradient(baseColor?: string | null): { from: string; to: string } {
  if (!baseColor || baseColor.length < 7) {
    return { from: '#393e27', to: '#282c1b' };
  }
  const { r, g, b } = hexToRgb(baseColor);
  const { h, s, l } = rgbToHsl(r, g, b);
  return {
    from: hslToHex(h, s, l),
    to: hslToHex(h, s, Math.max(0, l * 0.7)),
  };
}
